import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MerchantUserService } from 'app/services';
import { Utils } from 'app/utils';
import { MessageService } from 'primeng/api';
import * as XLSX from 'xlsx';
import { MerchantEmployee } from '../../../../../models/merchant/merchant-employee';
import { TypeMerchantUserEnum } from 'app/models';

@Component({
  selector: 'create-merchant-multiple-employee',
  templateUrl: './create-merchant-multiple-employee.component.html',
  styleUrls: ['./create-merchant-multiple-employee.component.scss'],
  providers: [MessageService]
})
export class CreateMerchantMultipleEmployeeComponent {

  constructor(
    private messageService: MessageService,
    public merchantUserService: MerchantUserService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ){}

  public employeesXls: MerchantEmployee[] = [];
  public loading: boolean = false;
  public errorUploadedEmployees: any[] = [];
  public totalUploadedEmployeesCount: number = 0;
  public errorUploadedEmployeesCount: number = 0;
  public successUploadedEmployeesCount: number = 0;
  progressInterval: any;
  progressBar: number = 0;
  public employeeType = TypeMerchantUserEnum;

  //Upload image
  public handleInputClick(id: string): void {
    document.getElementById(id)?.click();
  }

  public handleXlsChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.progressInterval = setInterval(() => {
        if (this.progressBar === 100) {
            clearInterval(this.progressInterval);
        } else {
            this.progressBar += 5;
        }
    }, 75);

    let file: File | null = null;

    if (target.files) {
        file = target.files[0];
    }

    if (!file) {
        return;
    }

    const formData = new FormData();
    formData.append('file', file);

    this.merchantUserService.uploadEmployeesXls(formData, this.messageService)
        .then(data => {
            if (!data.Success) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }
            this.employeesXls = data.Items;
            this.handleUploadClick();
        }).catch(err => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
        });
    }

    public async handleUploadClick(): Promise<any> {
            this.loading = true;
            const dtos = {
                Users: this.employeesXls.map(employee =>  {
                    const dto: MerchantEmployee = {
                        MerchantId: this.data.merchantId,
                        FirstName: employee.FirstName,
                        LastName: employee.LastName,
                        Birthday: employee.Birthday,
                        Email: employee.Email,
                        Phone: employee.Phone,
                        RoleId: Number(employee.RoleId),
                        StoreIds: employee.StoreIds,
                        ExternalId: employee.ExternalId,
                        Address: employee.Address,
                        DelayInMin: 10,
                        TimeZoneOffset: Utils.getTimezoneOffset(),
                        ImageUrl: employee.ImageUrl
                    };
                    return dto;
                })
            };

            this.merchantUserService.createMerchantUsers(dtos, this.messageService).then(data => {
                this.errorUploadedEmployees = data.FailedImportDtos;
                this.errorUploadedEmployeesCount = data.FailedImportDtos?.length ?? 0;
                this.totalUploadedEmployeesCount = this.employeesXls.length;
                this.successUploadedEmployeesCount = this.totalUploadedEmployeesCount - data.FailedImportDtos?.length;
                this.data.merchantEmployeesComponent.getMerchantEmployees(0);
                this.loading = false;
                if(!data?.Success){
                    this.messageService.add({ severity: 'error', summary: 'Warning', detail: data.ErrorMessage });
                    return;
                }
                if(data.FailedImportDtos?.length > 0 && this.successUploadedEmployeesCount > 0){
                    this.messageService.add({ severity: 'error', summary: 'Warning',
                        detail: 'Employee(s) has been partially uploaded. Please review the failed records'});
                    return;
                }
                if(data.FailedImportDtos?.length < 1 && this.successUploadedEmployeesCount > 0){
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'All employee(s) has been successfully uploaded.'});
                    return;
                }
                if(data.FailedImportDtos?.length > 0){
                    this.messageService.add({ severity: 'error', summary: 'Warning',
                        detail: 'Upload failed. The employee(s) may already exist or contain invalid data. Please review the failed records.' });
                    return;
                }
            }).catch(err => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Warning', detail: 'Oops! Something went wrong while uploading your data. Please try again.'});
                throw err;
            });
    }

    public downloadTemplate() {
        const dto = {
            MerchantId: this.data.merchantId
        };
        this.merchantUserService.getMerchantUserTemplate(dto)
            .subscribe({
                next: (data) => {
                    const fileName = 'Employees Template';
                    const blob: Blob = data.body as Blob;

                    if (!data.ok) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                        return;
                    }
                    const a = document.createElement('a');
                    a.download = fileName;
                    a.href = window.URL.createObjectURL(blob);
                    a.click();
                    a.remove();
                }, error: (err) => {
                    this.loading = false;
                    if (err.status === 500) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File not found' });
                    }
                    else {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
                    }
                }
            });
    }

    public saveUnuploadedEmployees() {
        const data = this.errorUploadedEmployees.map((item: MerchantEmployee) => ({
            FirstName: item.FirstName,
            LastName: item.LastName,
            Address: item.Address,
            Birthday: item.Birthday,
            ExternalId: item.ExternalId,
            Phone: item.Phone,
            Email: item.Email,
            RoleId: item.RoleId,
            StoreIds: item.StoreIds,
            ImageUrl: item.ImageUrl,
        }));

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        /* save to file */
        XLSX.writeFile(wb, 'Unsuccessfully loaded products.xlsx');
    }
}
